import React, { InputHTMLAttributes, forwardRef } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: React.ReactNode;
    error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, error, className = '', id, ...props }, ref) => {
        const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

        return (
            <div className="flex flex-col">
                <label
                    htmlFor={checkboxId}
                    className={`flex items-start gap-2 cursor-pointer group ${
                        props.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                    <div className="relative flex items-center">
                        <input
                            ref={ref}
                            type="checkbox"
                            id={checkboxId}
                            className="peer sr-only"
                            aria-invalid={error ? 'true' : 'false'}
                            aria-describedby={error ? `${checkboxId}-error` : undefined}
                            {...props}
                        />
                        <div
                            className={`
                                w-5 h-5 rounded border-2 flex items-center justify-center
                                transition-all duration-200
                                peer-checked:bg-[#015FA6] peer-checked:border-[#015FA6]
                                peer-focus:ring-2 peer-focus:ring-[#015FA6]/20
                                ${error ? 'border-red-500' : 'border-[#CBD5E0]'}
                                ${props.disabled ? '' : 'group-hover:border-[#015FA6]'}
                                ${className}
                            `}
                        >
                            <svg
                                className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.3332 4L5.99984 11.3333L2.6665 8"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                    {label && (
                        <span className="text-sm text-gray-700 font-manrope select-none pt-0.5">
                            {label}
                        </span>
                    )}
                </label>
                {error && (
                    <p
                        id={`${checkboxId}-error`}
                        role="alert"
                        className="mt-1.5 ml-7 text-xs text-red-500 font-manrope flex items-center gap-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                            <path
                                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {error}
                    </p>
                )}
            </div>
        );
    }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
